import { useState, useEffect } from "react";
import { api } from "../../App";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Check,
  X,
  Eye,
  Loader2,
  Shield,
  ClipboardList,
  Filter,
  Info,
  Download,
  ZoomIn,
  Phone,
  Mail,
  Clock,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_FILTERS = [
  { value: "pending", label: "Pendientes" },
  { value: "approved", label: "Aprobados" },
  { value: "rejected", label: "Rechazados" },
  { value: "needs_info", label: "Más información" },
  { value: "all", label: "Todos" },
];

const STATUS_CLASSES = {
  approved: "bg-green-100 text-green-700",
  pending: "bg-yellow-100 text-yellow-700",
  rejected: "bg-red-100 text-red-700",
  needs_info: "bg-blue-100 text-blue-700",
  not_started: "bg-neutral-100 text-neutral-700",
};

const ID_DOC_LABELS = {
  government_id_front: "INE / Pasaporte (frente)",
  government_id_back: "INE / Pasaporte (reverso)",
  driver_license_front: "Licencia (frente)",
  driver_license_back: "Licencia (reverso)",
  selfie: "Selfie con identificación",
};

const PER_DOC_STATUS_CLASSES = {
  approved: "bg-green-100 text-green-700",
  pending: "bg-amber-100 text-amber-700",
  rejected: "bg-red-100 text-red-700",
  needs_info: "bg-blue-100 text-blue-700",
  not_uploaded: "bg-neutral-100 text-neutral-500",
};

function statusBadge(status) {
  return (
    <Badge className={STATUS_CLASSES[status] || STATUS_CLASSES.not_started}>
      {status || "—"}
    </Badge>
  );
}

function DocThumb({ label, src }) {
  if (!src) {
    return (
      <div className="border border-dashed border-neutral-300 rounded-lg p-4 text-xs text-neutral-400 text-center">
        {label}
        <br />
        (No subido)
      </div>
    );
  }
  return (
    <a
      href={src}
      target="_blank"
      rel="noopener noreferrer"
      className="block group"
    >
      <div className="aspect-[4/3] rounded-lg overflow-hidden bg-neutral-100 border border-neutral-200">
        <img src={src} alt={label} className="w-full h-full object-cover group-hover:scale-105 transition" />
      </div>
      <p className="text-xs text-neutral-600 mt-1">{label}</p>
    </a>
  );
}

export default function AdminVerificationsTab() {
  const [kind, setKind] = useState("identity"); // identity | host
  const [filter, setFilter] = useState("pending");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [docContext, setDocContext] = useState(null); // {key, label} for per-doc actions
  const [rejectMode, setRejectMode] = useState("rejected"); // "rejected" | "needs_info"
  const [zoomDoc, setZoomDoc] = useState(null); // {url, label}

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "all") {
        if (kind === "identity") params.status = filter;
        else params.host_status = filter;
      }
      const { data } = await api.get("/admin/users", { params });
      setUsers(data.users || []);
    } catch {
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, filter]);

  const endpoint = (userId) =>
    kind === "identity"
      ? `/admin/users/${userId}/verification`
      : `/admin/users/${userId}/host-verification`;

  const approve = async (u, docKey = null) => {
    setSaving(true);
    try {
      const body = docKey ? { status: "approved", doc: docKey } : { status: "approved" };
      await api.put(endpoint(u.user_id), body);
      toast.success(docKey ? `Documento aprobado` : "Aprobado");
      // Refresh selected user data
      const { data } = await api.get("/admin/users", {
        params: kind === "identity" ? { } : { },
      });
      const refreshed = (data.users || []).find((x) => x.user_id === u.user_id);
      if (refreshed) setSelected(refreshed);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const openReject = (u, docKey = null, label = null, mode = "rejected") => {
    setSelected(u);
    setReason("");
    setDocContext(docKey ? { key: docKey, label } : null);
    setRejectMode(mode);
    setRejectDialog(true);
  };

  const confirmReject = async () => {
    if (!reason.trim()) {
      toast.error("El motivo es requerido");
      return;
    }
    setSaving(true);
    try {
      const body = docContext
        ? { status: rejectMode, reason, doc: docContext.key }
        : { status: rejectMode, reason };
      await api.put(endpoint(selected.user_id), body);
      toast.success(rejectMode === "needs_info" ? "Más información solicitada" : "Rechazado");
      setRejectDialog(false);
      setDocContext(null);
      // Refresh selected
      const { data } = await api.get("/admin/users", { params: {} });
      const refreshed = (data.users || []).find((x) => x.user_id === selected.user_id);
      if (refreshed) setSelected(refreshed);
      else setSelected(null);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const statusOf = (u) => (kind === "identity" ? u.verification_status : u.host_verification_status);

  return (
    <div className="space-y-6" data-testid="admin-verifications-tab">
      {/* Kind tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant={kind === "identity" ? "default" : "outline"}
          size="sm"
          onClick={() => setKind("identity")}
          className={kind === "identity" ? "bg-[#111111] hover:bg-neutral-800" : ""}
          data-testid="kind-identity-btn"
        >
          <Shield className="w-4 h-4 mr-2" />
          Identidad (Usuarios)
        </Button>
        <Button
          variant={kind === "host" ? "default" : "outline"}
          size="sm"
          onClick={() => setKind("host")}
          className={kind === "host" ? "bg-[#111111] hover:bg-neutral-800" : ""}
          data-testid="kind-host-btn"
        >
          <ClipboardList className="w-4 h-4 mr-2" />
          Propietario (Hosts)
        </Button>
      </div>

      {/* Status filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-neutral-600 flex items-center gap-1">
          <Filter className="w-3.5 h-3.5" />
          Filtro:
        </span>
        {STATUS_FILTERS.map((f) => (
          <Badge
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`cursor-pointer ${
              filter === f.value
                ? "bg-[#111111] text-white hover:bg-neutral-800"
                : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
            }`}
            data-testid={`filter-${f.value}`}
          >
            {f.label}
          </Badge>
        ))}
      </div>

      <div className="card-base p-0 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
          </div>
        ) : users.length === 0 ? (
          <div className="py-20 text-center text-neutral-500 text-sm">
            No hay usuarios con este estado
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Usuario</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.user_id}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="text-sm text-neutral-600">{u.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">{u.role}</Badge>
                  </TableCell>
                  <TableCell>{statusBadge(statusOf(u))}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelected(u)}
                      data-testid={`review-${u.user_id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Revisar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected && !rejectDialog} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-3xl" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle>
              {kind === "identity" ? "Verificación de Identidad" : "Verificación de Propietario"} ·{" "}
              {selected?.name}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 mt-4">
              {/* Account info bar */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 bg-neutral-50 border border-neutral-200 rounded-xl p-3 text-sm">
                <div className="flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-neutral-500" />
                  <span className="text-neutral-500">Email:</span>
                  <span className="font-medium truncate" data-testid="account-email">{selected.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 text-neutral-500" />
                  <span className="text-neutral-500">Teléfono:</span>
                  <span className="font-medium" data-testid="account-phone">{selected.phone || "—"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Info className="w-3.5 h-3.5 text-neutral-500" />
                  <span className="text-neutral-500">Tipo de cuenta:</span>
                  <Badge variant="outline" className="capitalize" data-testid="account-role">{selected.role}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5 text-neutral-500" />
                  <span className="text-neutral-500">Cuenta creada:</span>
                  <span className="font-medium">
                    {selected.created_at
                      ? new Date(selected.created_at).toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "numeric" })
                      : "—"}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <div className="text-sm">
                  <span className="text-neutral-500">Estado:</span>{" "}
                  {statusBadge(statusOf(selected))}
                </div>
              </div>

              {kind === "identity" ? (
                <>
                  <h4 className="font-semibold text-sm mt-4">Documentos de identidad</h4>
                  <p className="text-xs text-neutral-500 -mt-2">
                    Toca cualquier documento para aprobarlo, rechazarlo o pedir más información.
                  </p>

                  {/* Selfie ↔ ID Comparison (collapsible to keep doc cards visible) */}
                  {(() => {
                    const docs = selected.verification_docs || {};
                    const selfie = docs.selfie?.url;
                    const idFront = docs.driver_license_front?.url || docs.government_id_front?.url;
                    if (!selfie || !idFront) return null;
                    return (
                      <details
                        className="border border-blue-200 bg-blue-50/40 rounded-xl"
                        data-testid="selfie-id-comparison"
                      >
                        <summary className="cursor-pointer list-none p-3 text-xs font-semibold text-blue-900 flex items-center justify-between">
                          <span className="flex items-center gap-1">
                            <Eye className="w-3.5 h-3.5" />
                            Comparar selfie con identificación
                          </span>
                          <span className="text-[10px] text-blue-700">Toca para abrir</span>
                        </summary>
                        <div className="px-3 pb-3 grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => setZoomDoc({ url: selfie, label: "Selfie", key: "selfie" })}
                            className="text-left"
                          >
                            <img src={selfie} alt="Selfie" className="w-full aspect-[4/3] object-cover rounded-lg border border-blue-200 hover:opacity-90" />
                            <p className="text-[10px] text-neutral-600 mt-1">Selfie</p>
                          </button>
                          <button
                            type="button"
                            onClick={() => setZoomDoc({ url: idFront, label: "ID / Licencia", key: "id_front" })}
                            className="text-left"
                          >
                            <img src={idFront} alt="ID" className="w-full aspect-[4/3] object-cover rounded-lg border border-blue-200 hover:opacity-90" />
                            <p className="text-[10px] text-neutral-600 mt-1">ID / Licencia</p>
                          </button>
                        </div>
                      </details>
                    );
                  })()}

                  <div className="space-y-3">
                    {Object.entries(ID_DOC_LABELS).map(([key, label]) => {
                      const doc = (selected.verification_docs || {})[key] || {
                        url: null,
                        status: "not_uploaded",
                      };
                      const statusCls = PER_DOC_STATUS_CLASSES[doc.status] || PER_DOC_STATUS_CLASSES.not_uploaded;
                      const isPdf =
                        doc.url && typeof doc.url === "string" && doc.url.startsWith("data:application/pdf");
                      return (
                        <div
                          key={key}
                          className="border border-neutral-200 rounded-xl p-3 flex flex-col sm:flex-row sm:items-start gap-3"
                          data-testid={`admin-doc-${key}`}
                        >
                          {/* Thumb */}
                          <div className="w-full sm:w-28 flex-shrink-0">
                            {!doc.url ? (
                              <div className="aspect-[4/3] rounded-lg border border-dashed border-neutral-300 flex items-center justify-center text-xs text-neutral-400">
                                Sin subir
                              </div>
                            ) : isPdf ? (
                              <a
                                href={doc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="aspect-[4/3] rounded-lg bg-neutral-100 flex items-center justify-center text-xs font-medium hover:bg-neutral-200"
                              >
                                Ver PDF
                              </a>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setZoomDoc({ url: doc.url, label, key })}
                                className="block w-full text-left"
                                data-testid={`zoom-doc-${key}`}
                              >
                                <img
                                  src={doc.url}
                                  alt={label}
                                  className="aspect-[4/3] w-full object-cover rounded-lg border border-neutral-200 hover:opacity-90"
                                />
                              </button>
                            )}
                          </div>
                          {/* Info + actions */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="font-semibold text-sm">{label}</span>
                              <Badge className={`${statusCls} text-xs`}>{doc.status}</Badge>
                            </div>
                            {doc.uploaded_at && (
                              <p className="text-xs text-neutral-500 flex items-center gap-1 mb-1">
                                <Clock className="w-3 h-3" />
                                Subido el{" "}
                                {new Date(doc.uploaded_at).toLocaleString("es-MX", {
                                  dateStyle: "medium",
                                  timeStyle: "short",
                                })}
                              </p>
                            )}
                            {doc.rejection_reason && (
                              <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-1.5 mb-2">
                                Motivo: {doc.rejection_reason}
                              </p>
                            )}
                            {doc.url && (
                              <div className="flex items-center gap-2 flex-wrap">
                                <Button
                                  size="sm"
                                  onClick={() => approve(selected, key)}
                                  className="bg-green-600 hover:bg-green-700 h-7 text-xs"
                                  disabled={saving || doc.status === "approved"}
                                  data-testid={`approve-doc-${key}`}
                                >
                                  <Check className="w-3 h-3 mr-1" />
                                  Aprobar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openReject(selected, key, label, "rejected")}
                                  className="border-red-300 text-red-600 hover:bg-red-50 h-7 text-xs"
                                  disabled={saving || doc.status === "rejected"}
                                  data-testid={`reject-doc-${key}`}
                                >
                                  <X className="w-3 h-3 mr-1" />
                                  Rechazar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openReject(selected, key, label, "needs_info")}
                                  className="border-blue-300 text-blue-700 hover:bg-blue-50 h-7 text-xs"
                                  disabled={saving || doc.status === "needs_info"}
                                  data-testid={`needs-info-doc-${key}`}
                                >
                                  <Info className="w-3 h-3 mr-1" />
                                  Más info
                                </Button>
                                {!isPdf && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setZoomDoc({ url: doc.url, label, key })}
                                    className="h-7 text-xs text-neutral-600"
                                    data-testid={`zoom-btn-${key}`}
                                  >
                                    <ZoomIn className="w-3 h-3 mr-1" />
                                    Zoom
                                  </Button>
                                )}
                                <a
                                  href={doc.url}
                                  download={`${selected.user_id}_${key}.${isPdf ? "pdf" : "jpg"}`}
                                  className="inline-flex items-center gap-1 h-7 px-2 text-xs text-neutral-600 hover:bg-neutral-100 rounded-md"
                                  data-testid={`download-doc-${key}`}
                                >
                                  <Download className="w-3 h-3" />
                                  Descargar
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <>
                  <h4 className="font-semibold text-sm mt-4">Documentos de propietario</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <DocThumb label="Comprobante domicilio" src={selected.proof_of_address} />
                    <DocThumb label="Factura vehículo" src={selected.vehicle_ownership_doc} />
                  </div>
                  {selected.host_verification_rejection_reason && (
                    <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-2">
                      Rechazo previo: {selected.host_verification_rejection_reason}
                    </div>
                  )}
                </>
              )}

              <div className="flex items-center justify-end gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => openReject(selected)}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  disabled={saving}
                  data-testid="reject-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Rechazar todo
                </Button>
                <Button
                  onClick={() => approve(selected)}
                  className="bg-green-600 hover:bg-green-700"
                  disabled={saving}
                  data-testid="approve-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                  Aprobar todo
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject / Needs-info dialog */}
      <Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>
              {rejectMode === "needs_info"
                ? docContext
                  ? `Solicitar más información: ${docContext.label}`
                  : "Solicitar más información"
                : docContext
                ? `Rechazar: ${docContext.label}`
                : "Motivo del rechazo"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            <Label>
              {rejectMode === "needs_info"
                ? "Indica al usuario qué información necesitas"
                : docContext
                ? "Explica por qué se rechaza este documento"
                : "Explica brevemente por qué se rechazan los documentos"}
            </Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              placeholder={
                rejectMode === "needs_info"
                  ? "Ej: Por favor envía una foto del reverso de tu licencia con buena iluminación."
                  : "Ej: La licencia está vencida; por favor sube una licencia vigente."
              }
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              onClick={confirmReject}
              className={
                rejectMode === "needs_info"
                  ? "bg-blue-600 hover:bg-blue-700"
                  : "bg-red-600 hover:bg-red-700"
              }
              disabled={saving}
              data-testid="confirm-reject-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {rejectMode === "needs_info" ? "Solicitar información" : "Confirmar rechazo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image zoom / fullscreen viewer */}
      {zoomDoc && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setZoomDoc(null)}
          data-testid="image-zoom-viewer"
        >
          <img
            src={zoomDoc.url}
            alt={zoomDoc.label}
            className="max-w-[95vw] max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <div
            className="absolute top-4 right-4 flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <a
              href={zoomDoc.url}
              download={`zoom_${zoomDoc.key}.jpg`}
              className="inline-flex items-center gap-1 bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-md text-sm"
              data-testid="zoom-download-btn"
            >
              <Download className="w-3.5 h-3.5" />
              Descargar
            </a>
            <button
              onClick={() => setZoomDoc(null)}
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
              data-testid="zoom-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm px-3 py-1 rounded-md">
            {zoomDoc.label}
          </div>
        </div>
      )}
    </div>
  );
}
